import { useState, useEffect, useMemo } from "react";
import {
  Search,
  CreditCard,
  User,
  CheckCircle,
  Clock,
  Loader2,
  Trash2,
  Plus,
  Save,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, {
  getAllUsers,
  getSubscriptionPlans,
  assignSubscription,
  removeSubscription,
  createSubscriptionPlan,
  deleteSubscriptionPlan,
  updateSubscriptionPlan,
} from "@/services/api";

export const Subscriptions = () => {
  // Existing state from Subscriptions.jsx
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [activeSubSearch, setActiveSubSearch] = useState("");
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [renewalSearch, setRenewalSearch] = useState("");
  const [isRenewing, setIsRenewing] = useState(false);

  // New state migrated from ContentManagement.jsx
  const [isFetchingPlans, setIsFetchingPlans] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialPlanState = {
    id: "",
    name: "",
    quantity: "",
    price: "",
    coins_bonus: "",
    duration: "1 month",
    pickups: "",
    discount: "",
    features: [""],
  };

  const [newPlan, setNewPlan] = useState(initialPlanState);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [usersData, plansData, subsData] = await Promise.all([
        getAllUsers("user"),
        getSubscriptionPlans(),
        api.get("/subscriptions/active"),
      ]);

      if (usersData && usersData.success) {
        setUsers(usersData.users);
      }
      if (plansData && plansData.status === 200) {
        setPlans(plansData.data.plans || []);
      }
      if (subsData && subsData.data.success) {
        setActiveSubscriptions(subsData.data.subscriptions || []);
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Logic for Plan Management (Migrated)
  const fetchPlans = async () => {
    setIsFetchingPlans(true);
    try {
      const response = await getSubscriptionPlans();
      if (response.status === 200) {
        setPlans(response.data.plans || []);
      } else {
        toast.error(response.data.message || "Failed to fetch subscription plans");
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      toast.error("An error occurred while fetching plans");
    } finally {
      setIsFetchingPlans(false);
    }
  };

  const handleAddFeature = () => {
    setNewPlan({ ...newPlan, features: [...newPlan.features, ""] });
  };

  const handleRemoveFeature = (index) => {
    const updatedFeatures = newPlan.features.filter((_, i) => i !== index);
    setNewPlan({ ...newPlan, features: updatedFeatures });
  };

  const handleFeatureChange = (index, value) => {
    const updatedFeatures = [...newPlan.features];
    updatedFeatures[index] = value;
    setNewPlan({ ...newPlan, features: updatedFeatures });
  };

  const handleEditClick = (plan) => {
    setNewPlan({
      ...plan,
      features: plan.features || [""],
    });
    setIsEditMode(true);
    setIsDialogOpen(true);
  };

  const handleDeletePlan = async (id) => {
    try {
      const response = await deleteSubscriptionPlan(id);
      if (response.status === 204) {
        toast.success("Subscription plan deleted successfully");
        setPlans((prev) =>
          prev.filter((plan) => (plan.id || plan.docId) !== id)
        );
      } else {
        toast.error(response.data.message || "Failed to delete subscription plan");
      }
    } catch (error) {
      console.error("Error deleting plan:", error);
      toast.error("An error occurred while deleting the plan");
    }
  };

  const handleSavePlan = async () => {
    if (!newPlan.id || typeof newPlan.id !== "string") {
      toast.error("Plan ID is required");
      return;
    }
    if (!newPlan.name || typeof newPlan.name !== "string") {
      toast.error("Plan Name is required");
      return;
    }

    const planData = {
      ...newPlan,
      features: newPlan.features.filter((f) => f.trim() !== ""),
    };

    setIsSubmitting(true);
    try {
      const processedData = {
        ...planData,
        price: Number(planData.price) || 0,
        coins_bonus: Number(planData.coins_bonus) || 0,
        discount: Number(planData.discount) || 0,
        pickup_count: Number(planData.pickup_count) || 0,
      };

      if (isEditMode) {
        const response = await updateSubscriptionPlan(
          newPlan.id || newPlan.docId,
          processedData
        );
        if (response.status === 200) {
          toast.success(response.data.message || "Plan updated successfully!");
          setNewPlan(initialPlanState);
          setIsDialogOpen(false);
          fetchPlans();
        } else {
          toast.error(response.data.message || "Failed to update plan");
        }
      } else {
        const response = await createSubscriptionPlan(processedData);
        if (response.status === 201) {
          toast.success(
            response.data.message || `Plan \"${newPlan.name}\" created successfully!`
          );
          setNewPlan(initialPlanState);
          setIsDialogOpen(false);
          fetchPlans();
        } else {
          toast.error(response.data.message || "Failed to create plan");
        }
      }
    } catch (error) {
      console.error("Error saving plan:", error);
      toast.error("An error occurred while saving the plan");
      setIsDialogOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Logic for Manual Assignment
  const filteredUsers = useMemo(() => {
    const search = userSearch.trim().toLowerCase();
    if (!search || search.length < 2) return [];

    return users
      .filter((user) => {
        const name = (user.name || user.displayName || "").toLowerCase();
        const contact = (user.contact || user.phone || "").toLowerCase();
        const email = (user.email || "").toLowerCase();

        return (
          name.includes(search) || contact.includes(search) || email.includes(search)
        );
      })
      .slice(0, 10);
  }, [users, userSearch]);

  const filteredActiveSubscriptions = useMemo(() => {
    const search = activeSubSearch.trim().toLowerCase();
    if (!search) return activeSubscriptions;

    return activeSubscriptions.filter((sub) => {
      const name = (sub.user_name || "").toLowerCase();
      const phone = (sub.user_phone || "").toLowerCase();
      return name.includes(search) || phone.includes(search);
    });
  }, [activeSubscriptions, activeSubSearch]);

  const handleRemove = async (subId) => {
    if (!window.confirm("Are you sure you want to remove this subscription?"))
      return;

    try {
      const response = await removeSubscription(subId);
      if (response.success) {
        toast.success("Subscription removed successfully");
        fetchInitialData();
      } else {
        toast.error(response.message || "Failed to remove subscription");
      }
    } catch (error) {
      console.error("Error removing subscription:", error);
      toast.error("An error occurred while removing");
    }
  };

  const handleAssign = async () => {
    if (!selectedUserId || !selectedPlanId || !startDate) {
      toast.error("Please select a user, a plan, and a start date");
      return;
    }

    setIsAssigning(true);
    try {
      const response = await assignSubscription({
        userId: selectedUserId,
        planId: selectedPlanId,
        startDate: startDate,
      });

      if (response.success) {
        toast.success("Subscription assigned successfully");
        setSelectedUserId("");
        setSelectedPlanId("");
        setUserSearch("");
        setStartDate(new Date().toISOString().split('T')[0]);
        fetchInitialData();
      } else {
        toast.error(response.message || "Failed to assign subscription");
      }
    } catch (error) {
      console.error("Error assigning subscription:", error);
      toast.error(error.response?.data?.message || "An error occurred");
    } finally {
      setIsAssigning(false);
    }
  };

  // Renewals Logic
  const renewalSubscriptions = useMemo(() => {
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    return activeSubscriptions.filter(sub => {
      const endDate = new Date(sub.end_date);
      return endDate <= thirtyDaysFromNow && endDate > now;
    }).sort((a, b) => new Date(a.end_date) - new Date(b.end_date));
  }, [activeSubscriptions]);

  const filteredRenewals = useMemo(() => {
    const search = renewalSearch.trim().toLowerCase();
    if (!search) return renewalSubscriptions;

    return renewalSubscriptions.filter((sub) => {
      const name = (sub.user_name || "").toLowerCase();
      const phone = (sub.user_phone || "").toLowerCase();
      return name.includes(search) || phone.includes(search);
    });
  }, [renewalSubscriptions, renewalSearch]);

  const getExpiryStatus = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft <= 7) return { variant: "destructive", label: "Urgent", daysLeft };
    if (daysLeft <= 14) return { variant: "warning", label: "Soon", daysLeft };
    return { variant: "secondary", label: "Upcoming", daysLeft };
  };

  const [activeTab, setActiveTab] = useState("plans");

  const handleRenewSubscription = (userId, userName, userPhone) => {
    setSelectedUserId(userId);
    const phone = userPhone || "No contact";
    setUserSearch(`${userName || "Unknown"} (${phone})`);
    setActiveTab("assign");
  };

  const selectedUser = users.find((u) => u._id === selectedUserId);

  if (loading && plans.length === 0) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Subscription Management</h1>
        <p className="text-muted-foreground mt-1">
          Manage subscription plans and active user subscriptions
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="plans">Subscription Plans</TabsTrigger>
          <TabsTrigger value="active">Active Subscriptions</TabsTrigger>
          <TabsTrigger value="renewals">Renewals</TabsTrigger>
          <TabsTrigger value="assign">Assign Plan</TabsTrigger>
        </TabsList>

        {/* Plans Management Tab */}
        <TabsContent value="plans" className="space-y-6">
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Subscription Plans</CardTitle>
                <CardDescription>
                  Create and manage subscription plans for your users
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchPlans}
                disabled={isFetchingPlans}
              >
                {isFetchingPlans ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Refresh"
                )}
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {plans.map((plan) => (
                  <Card
                    key={plan.id || plan.docId}
                    className="border-border relative flex flex-col"
                  >
                    {plan.id === "premium" && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                        Most Popular
                      </div>
                    )}
                    <CardHeader>
                      <CardTitle className="text-xl">{plan.name}</CardTitle>
                      <CardDescription>{plan.quantity}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4 flex-grow flex flex-col">
                      <div className="text-3xl font-bold">
                        ₹{plan.price}
                        <span className="text-sm font-normal text-muted-foreground">
                          /{plan.duration}
                        </span>
                      </div>
                      {plan.pickup_count > 0 && (
                        <div className="text-sm font-medium text-primary">
                          {plan.pickup_count} pickups included
                        </div>
                      )}
                      <div className="flex-grow">
                        {plan.features && plan.features.length > 0 ? (
                          <ul className="space-y-2 text-sm">
                            {plan.features.slice(0, 4).map((feature, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                                <span className="line-clamp-1">{feature}</span>
                              </li>
                            ))}
                            {plan.features.length > 4 && (
                              <li className="text-xs text-muted-foreground">
                                +{plan.features.length - 4} more features
                              </li>
                            )}
                          </ul>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">
                            No features listed
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 pt-4 mt-auto">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => handleEditClick(plan)}
                        >
                          Edit
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone. This will permanently delete
                                the subscription plan \"{plan.name}\" from our servers.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeletePlan(plan.id || plan.docId)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Add New Plan Dialog */}
                <Dialog
                  open={isDialogOpen}
                  onOpenChange={(open) => {
                    setIsDialogOpen(open);
                    if (!open) {
                      setIsEditMode(false);
                      setNewPlan(initialPlanState);
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <div className="border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center p-6 text-center space-y-4 hover:bg-muted/50 transition-colors cursor-pointer min-h-[250px]">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Plus className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">Add New Plan</h4>
                        <p className="text-sm text-muted-foreground">
                          Create a custom subscription plan
                        </p>
                      </div>
                    </div>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {isEditMode
                          ? "Edit Subscription Plan"
                          : "Create New Subscription Plan"}
                      </DialogTitle>
                      <DialogDescription>
                        {isEditMode
                          ? "Update the details for the subscription plan."
                          : "Fill in the details for the new subscription plan."}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="plan-id">Plan ID</Label>
                          <Input
                            id="plan-id"
                            value={newPlan.id}
                            onChange={(e) =>
                              setNewPlan({ ...newPlan, id: e.target.value })
                            }
                            placeholder="e.g. test_plan"
                            disabled={isEditMode}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="plan-name">Plan Name</Label>
                          <Input
                            id="plan-name"
                            value={newPlan.name}
                            onChange={(e) =>
                              setNewPlan({ ...newPlan, name: e.target.value })
                            }
                            placeholder="e.g. Premium Plan"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="plan-duration">Duration</Label>
                          <Input
                            id="plan-duration"
                            value={newPlan.duration}
                            onChange={(e) =>
                              setNewPlan({ ...newPlan, duration: e.target.value })
                            }
                            placeholder="e.g. 1 month"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="plan-price">Price (₹)</Label>
                          <Input
                            id="plan-price"
                            type="number"
                            value={newPlan.price}
                            onChange={(e) =>
                              setNewPlan({ ...newPlan, price: e.target.value })
                            }
                            onFocus={(e) =>
                              e.target.value === "0" &&
                              setNewPlan({ ...newPlan, price: "" })
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="plan-quantity">Quantity</Label>
                          <Input
                            id="plan-quantity"
                            value={newPlan.quantity}
                            onChange={(e) =>
                              setNewPlan({ ...newPlan, quantity: e.target.value })
                            }
                            placeholder="e.g. 155 or 10 kg/month"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="plan-coins">Coins Bonus</Label>
                          <Input
                            id="plan-coins"
                            type="number"
                            value={newPlan.coins_bonus}
                            onChange={(e) =>
                              setNewPlan({ ...newPlan, coins_bonus: e.target.value })
                            }
                            onFocus={(e) =>
                              e.target.value === "0" &&
                              setNewPlan({ ...newPlan, coins_bonus: "" })
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="plan-discount">Discount (%)</Label>
                          <Input
                            id="plan-discount"
                            type="number"
                            value={newPlan.discount}
                            onChange={(e) =>
                              setNewPlan({ ...newPlan, discount: e.target.value })
                            }
                            onFocus={(e) =>
                              e.target.value === "0" &&
                              setNewPlan({ ...newPlan, discount: "" })
                            }
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="plan-pickup-count">Pickup Count</Label>
                          <Input
                            id="plan-pickup-count"
                            type="number"
                            value={newPlan.pickup_count}
                            onChange={(e) =>
                              setNewPlan({ ...newPlan, pickup_count: e.target.value })
                            }
                            placeholder="e.g. 12"
                          />
                          <p className="text-xs text-muted-foreground">Number of pickups included (e.g. 12 pickups/month)</p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="plan-pickups">Pickups Info</Label>
                          <Input
                            id="plan-pickups"
                            value={newPlan.pickups}
                            onChange={(e) =>
                              setNewPlan({ ...newPlan, pickups: e.target.value })
                            }
                            placeholder="e.g. 10 pickups/month"
                          />
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Plan Features</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleAddFeature}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Feature
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {newPlan.features.map((feature, index) => (
                            <div key={index} className="flex gap-2">
                              <Input
                                value={feature}
                                onChange={(e) =>
                                  handleFeatureChange(index, e.target.value)
                                }
                                placeholder={`Feature ${index + 1}`}
                                className="flex-1"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => handleRemoveFeature(index)}
                                disabled={newPlan.features.length === 1}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setIsDialogOpen(false)}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSavePlan}
                        disabled={!newPlan.name || (!isEditMode && !newPlan.id) || isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {isEditMode ? "Updating..." : "Creating..."}
                          </>
                        ) : isEditMode ? (
                          "Update Plan"
                        ) : (
                          "Create Plan"
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Active Subscriptions Tab */}
        <TabsContent value="active">
          <Card className="border-border">
            <CardHeader className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-success" />
                  Active Subscriptions
                  <Badge variant="secondary" className="ml-2">
                    {filteredActiveSubscriptions.length} Records
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Currently active user subscriptions across the platform.
                </CardDescription>
              </div>
              <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or phone..."
                  value={activeSubSearch}
                  onChange={(e) => setActiveSubSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredActiveSubscriptions.length > 0 ? (
                      filteredActiveSubscriptions.map((sub) => (
                        <TableRow key={sub._id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">
                                {sub.user_name || "Unknown User"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {sub.user_phone}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {sub.plan_type}
                            </Badge>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              ₹{sub.amount}
                            </p>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5 text-sm">
                              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                              {new Date(sub.end_date).toLocaleDateString()}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="success" className="capitalize">
                              {sub.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:bg-destructive/10"
                              onClick={() => handleRemove(sub._id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="h-24 text-center text-muted-foreground"
                        >
                          No active subscriptions found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Renewals Tab */}
        <TabsContent value="renewals">
          <Card className="border-border">
            <CardHeader className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-warning" />
                  Upcoming Renewals
                  <Badge variant="secondary" className="ml-2">
                    {filteredRenewals.length} Users
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Users whose subscriptions are expiring within 30 days. Sorted by nearest expiry first.
                </CardDescription>
              </div>
              <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or phone..."
                  value={renewalSearch}
                  onChange={(e) => setRenewalSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Expires On</TableHead>
                      <TableHead>Days Left</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRenewals.length > 0 ? (
                      filteredRenewals.map((sub) => {
                        const expiryStatus = getExpiryStatus(sub.end_date);
                        return (
                          <TableRow key={sub._id} className={expiryStatus.variant === "destructive" ? "bg-destructive/5" : ""}>
                            <TableCell>
                              <div>
                                <p className="font-medium">
                                  {sub.user_name || "Unknown User"}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {sub.user_phone}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {sub.plan_type}
                              </Badge>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                ₹{sub.amount}
                              </p>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5 text-sm">
                                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                                {new Date(sub.end_date).toLocaleDateString()}
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="font-semibold">
                                {expiryStatus.daysLeft} days
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge variant={expiryStatus.variant} className="capitalize">
                                {expiryStatus.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                onClick={() => handleRenewSubscription(sub.user_id, sub.user_name, sub.user_phone)}
                              >
                                {isRenewing ? (
                                  <>
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    Renewing...
                                  </>
                                ) : (
                                  "Renew"
                                )}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="h-24 text-center text-muted-foreground"
                        >
                          <div className="flex flex-col items-center gap-2">
                            <CheckCircle className="w-8 h-8 text-success opacity-50" />
                            <p>No subscriptions expiring soon! All users are up to date.</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assign Plan Tab */}
        <TabsContent value="assign">
          <Card className="max-w-2xl mx-auto border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                Assign Plan
              </CardTitle>
              <CardDescription>
                Select a user and choose a subscription plan to assign manually.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Search User</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Name, Phone or Email..."
                    value={userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      if (selectedUserId) setSelectedUserId("");
                    }}
                    className="pl-10"
                  />
                </div>
                {userSearch && !selectedUserId && (
                  <div className="absolute w-full max-w-lg mt-1 border rounded-md bg-card shadow-lg overflow-hidden z-50 max-h-60 overflow-y-auto">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <button
                          key={user._id}
                          className="w-full text-left px-3 py-2 hover:bg-muted transition-colors border-b last:border-0"
                          onClick={() => {
                            setSelectedUserId(user._id);
                            const phone = user.contact || user.phone || "No contact";
                            setUserSearch(`${user.name || "Unknown"} (${phone})`);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-primary" />
                            <div>
                              <p className="text-sm font-medium">
                                {user.name || "Unknown"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {(user.contact || user.phone) && (
                                  <span>{user.contact || user.phone}</span>
                                )}
                                {user.email && (
                                  <span>
                                    {(user.contact || user.phone) ? " | " : ""}
                                    {user.email}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        No users found matching \"{userSearch}\"
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedUser && (
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/10 space-y-1">
                  <p className="text-xs font-semibold text-primary uppercase">
                    Selected User
                  </p>
                  <p className="text-sm font-medium">
                    {selectedUser.name || selectedUser.displayName || "Unknown"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {selectedUser.contact || selectedUser.phone || "No contact"}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Select Plan <span className="text-destructive">*</span></label>
                  <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.name} - ₹{plan.price}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Start Date <span className="text-destructive">*</span></label>
                  <Input 
                    type="date" 
                    value={startDate} 
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              <Button
                className="w-full"
                onClick={handleAssign}
                disabled={!selectedUserId || !selectedPlanId || !startDate || isAssigning}
              >
                {isAssigning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Assigning...
                  </>
                ) : (
                  "Assign Subscription"
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Subscriptions;
